import React, { useMemo, useState } from "react";
import { Cartesian2 } from "@cesium/engine";
import { useCesiumContext } from "./CesiumContext";
import { useCesiumEvent } from "./map";
import { throttle } from "lodash-es";
import { isDefined } from "../../lib/utils";
import QMViewer from "src/lib/cesium/QuickMapViewer";
import ScreenSpacePoint from "./ScreenSpacePoint";

export function useCenterResolution() {
  const [resolution, setResolution] = React.useState<number>();

  const { map: viewer } = useCesiumContext();
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const updateResolution = React.useCallback(() => {
    if (!viewer || viewer.isDestroyed() || !viewer?.scene?.camera) return;
      const res = viewer.getResolutionAtWindowPosition(getResolutionRefPoint(viewer));

      if (res === undefined) {
        setResolution(undefined);
        return;
      }
      setResolution(res);
      const { scene } = viewer;
      const width = scene.canvas.clientWidth;
      const height = scene.canvas.clientHeight;
      const ctx = scene.canvas.getContext("2d");
      if (!ctx) return;
      ctx.fillStyle = "#FF0000";
      ctx.fillRect((width / 2) - 5, (height / 2) - 5, (width / 2) + 5, (height / 2) + 5);
  }, [viewer]);

  const handlerFn = useMemo(
    () =>
      throttle(() => {
        updateResolution();
      }, 500),
    [viewer]
  );

  const camera = viewer?.isDestroyed() ? null : viewer?.scene?.camera;

  useCesiumEvent(camera, "changed", handlerFn);
  useCesiumEvent(camera, "moveStart", () => {
    // Clear any existing timeout to avoid overlap
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    // Set a timeout to guarantee an update if the camera keeps moving
    timeoutRef.current = setInterval(() => {
      handlerFn();
    }, 500);
  });
  useCesiumEvent(camera, "moveEnd", () => {
    // Clear any existing timeout and call the update function immediately
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    updateResolution();
  });

  if (!viewer || viewer.isDestroyed() || !viewer?.scene?.camera) return null;
  return resolution;
}

function getResolutionRefPoint(viewer: QMViewer) {
  const { scene } = viewer;
  const width = scene.canvas.clientWidth;
  const height = scene.canvas.clientHeight;
  const refPoint = new Cartesian2((width / 2) | 0, (height / 2) | 0);
  return refPoint;
}

const MAX_WIDTH = 100;
const DISTANCES = [
  0.01, 0.1, 1, 2, 3, 5, 10, 20, 30, 50, 100, 200, 300, 500, 1000, 2000, 3000,
  5000, 10000, 20000, 30000, 50000, 100000, 200000, 300000, 500000, 1000000,
  2000000, 3000000, 5000000, 10000000, 20000000, 30000000, 50000000,
];
export default function ScaleBar() {
  const { map: viewer } = useCesiumContext();
  const pointResolution = useCenterResolution();
  const [showMarker, setShowMarker] = useState(false);
  const [markerPosition, setMarkerPosition] = useState([0,0]);
  const scaleBarOptions = useMemo(() => {
    if (!pointResolution) return {};
    let distance;
    for (let i = DISTANCES.length - 1; !distance && i >= 0; --i) {
      if (DISTANCES[i] / pointResolution < MAX_WIDTH) {
        distance = DISTANCES[i];
        break;
      }
    }
    let label;
    if (!isDefined(distance)) return {};
    if (distance >= 1000) {
      label = (distance / 1000).toString() + " km";
    } else {
      label = distance.toString() + " m";
    }
    const width = (distance / pointResolution) | 0;
    return {
      width,
      label,
    };
  }, [pointResolution]);
  if (!pointResolution) return null;


  const handleMouseEnter = () => {
    const refPoint = getResolutionRefPoint(viewer);
    setMarkerPosition([refPoint.x, refPoint.y]);
    setShowMarker(true);
  };

  const handleMouseLeave = () => {
    setShowMarker(false);
  };

  return (
    <div className="scale-line w-[150px] text-[10px] mb-2"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      >
      <label className="block relative p-0 m-0 mt-1 mb-0.5 text-center whitespace-pre-wrap">
        {scaleBarOptions.label}
      </label>
      <div
        className="block h-1 p-0 mx-auto mb-1 text-left bg-qmdark-50 transition-all"
        style={{ width: `${scaleBarOptions.width}px` }}
      ></div>
      {showMarker && <ScreenSpacePoint position={markerPosition}/>}
    </div>
  );
}
